import { SPAWN_KINDS, type Color, type Config, type SpawnKind } from '@ml/config';
import { weightedPick, type Rng } from '../rng.ts';
import { countCells, createEmptyBoard, hasOverlay, indexOf } from './grid.ts';
import type { Board, Cell, Coord, OrbKind } from './types.ts';

export type SpawnConfig = Pick<Config, 'board' | 'essence'>;

/**
 * Sinh 1 viên (core-loop 3.3). Rút kind theo `board.spawn.kindWeights` duyệt theo thứ tự
 * `SPAWN_KINDS`; nếu ra `essence` mà số ngọc ấn trên bảng đã đạt `essenceMaxOnBoard` thì
 * đổi thành `basic` (không rút lại). Kind cần màu thì rút màu theo `colorWeights`, duyệt theo
 * thứ tự `board.colors`. Mỗi viên tốn đúng 1 lần rút kind + 0/1 lần rút màu.
 */
export function spawnCell(rng: Rng, config: SpawnConfig, essenceOnBoard: number): Cell {
  const sp = config.board.spawn;
  let kind: SpawnKind = weightedPick(rng, SPAWN_KINDS, (k) => sp.kindWeights[k] ?? 0);
  if (kind === 'essence') {
    if (essenceOnBoard < sp.essenceMaxOnBoard) {
      return { t: 'essence', charge: config.essence.startCharge };
    }
    kind = 'basic';
  }
  const color: Color = weightedPick(rng, config.board.colors, (c) => sp.colorWeights[c] ?? 0);
  return { t: 'orb', kind: kind as OrbKind, color };
}

export type SpawnEvent = { readonly x: number; readonly y: number; readonly cell: Cell };

function countEssence(board: Board): number {
  return countCells(board, (c) => c.t === 'essence');
}

/**
 * Lấp các ô rỗng (core-loop 3.4 bước 2): cột x = 0 → W-1, trong cột từ ô rỗng thấp nhất lên
 * trên. Giới hạn ngọc ấn tính cả viên vừa sinh trong lần lấp này.
 */
export function refill(
  board: Board,
  rng: Rng,
  config: SpawnConfig,
): { board: Board; spawned: SpawnEvent[] } {
  const cells = board.cells.slice();
  let essence = countEssence(board);
  const spawned: SpawnEvent[] = [];
  for (let x = 0; x < board.width; x++) {
    for (let y = board.height - 1; y >= 0; y--) {
      const i = indexOf(board, x, y);
      if (cells[i] !== null) continue;
      const cell = spawnCell(rng, config, essence);
      if (cell.t === 'essence') essence++;
      cells[i] = cell;
      spawned.push({ x, y, cell });
    }
  }
  return { board: { width: board.width, height: board.height, cells }, spawned };
}

export type FallEvent = { readonly from: Coord; readonly to: Coord };

/**
 * Trọng lực (core-loop 3.4 bước 1): mỗi cột mọi viên trượt xuống giữ thứ tự, ô rỗng dồn lên
 * đầu cột. Mọi viên đều rơi (kể cả khoá/băng/đánh dấu, booster, ngọc ấn; lớp phủ đi theo).
 * `moves` theo cột x tăng, trong cột từ dưới lên; chỉ gồm viên thật sự đổi chỗ.
 */
export function applyGravity(board: Board): { board: Board; moves: FallEvent[] } {
  const cells = board.cells.slice();
  const moves: FallEvent[] = [];
  for (let x = 0; x < board.width; x++) {
    let write = board.height - 1;
    for (let y = board.height - 1; y >= 0; y--) {
      const cell = board.cells[indexOf(board, x, y)] ?? null;
      if (cell === null) continue;
      if (write !== y) {
        cells[indexOf(board, x, write)] = cell;
        cells[indexOf(board, x, y)] = null;
        moves.push({ from: { x, y }, to: { x, y: write } });
      }
      write--;
    }
  }
  return { board: { width: board.width, height: board.height, cells }, moves };
}

/** Bảng mới sinh toàn bộ theo thứ tự hàng (core-loop 3.5), chưa kiểm nước đi. */
export function generateBoard(rng: Rng, config: SpawnConfig): Board {
  const { width, height } = config.board;
  const board = createEmptyBoard(width, height);
  const cells = board.cells.slice();
  let essence = 0;
  for (let i = 0; i < cells.length; i++) {
    const cell = spawnCell(rng, config, essence);
    if (cell.t === 'essence') essence++;
    cells[i] = cell;
  }
  return { width, height, cells };
}

/**
 * Sinh lại các ô không có lớp phủ theo thứ tự hàng (viên có lớp phủ đứng yên). Viên giữ lại
 * đều là orb thường (chỉ orb mang lớp phủ) nên giới hạn ngọc ấn đếm từ 0.
 */
export function regenerateUncovered(board: Board, rng: Rng, config: SpawnConfig): Board {
  const cells = board.cells.map((c) => (hasOverlay(c) ? c : null));
  let essence = 0;
  for (let i = 0; i < cells.length; i++) {
    if (cells[i] !== null) continue;
    const cell = spawnCell(rng, config, essence);
    if (cell.t === 'essence') essence++;
    cells[i] = cell;
  }
  return { width: board.width, height: board.height, cells };
}
